// The metric registry (AWARDS.md "The framework"): small named functions
// from a fact to a number. Pickers name these; nothing else computes.
// A metric returns null when the fact is not eligible (a bye, a loss for
// a "points in a win" metric), and the picker skips it.

import type {
  MilestoneMetricId,
  Minted,
  PastSeasonTeam,
  SeasonFacts,
  TeamPeriodFact,
  TeamPeriodMetricId,
  TeamSeasonMetricId,
} from './types';

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// ── Team, one period ──────────────────────────────────────────────────────

export const TEAM_PERIOD_METRICS: Record<TeamPeriodMetricId, (t: TeamPeriodFact) => number | null> =
  {
    points: (t) => (t.played ? t.score : null),
    benchPoints: (t) => (t.played ? t.benchPoints : null),
    leftOnBench: (t) =>
      t.played && t.optimalPoints !== null ? round1(Math.max(0, t.optimalPoints - t.score)) : null,
    marginOfVictory: (t) =>
      t.result === 'win' && t.opponentScore !== null ? round1(t.score - t.opponentScore) : null,
    marginOfDefeat: (t) =>
      t.result === 'loss' && t.opponentScore !== null ? round1(t.opponentScore - t.score) : null,
    pointsInLoss: (t) => (t.result === 'loss' ? t.score : null),
    pointsInWin: (t) => (t.result === 'win' ? t.score : null),
    overProjection: (t) =>
      t.played && t.projected !== null ? round1(t.score - t.projected) : null,
    underProjection: (t) =>
      t.played && t.projected !== null ? round1(t.projected - t.score) : null,
    topStarterPoints: (t) => (t.played && t.topStarter ? t.topStarter.points : null),
    topStarterShare: (t) =>
      t.played && t.topStarter && t.score > 0
        ? Math.round((t.topStarter.points / t.score) * 100)
        : null,
  };

// ── Team, the season so far ───────────────────────────────────────────────

/**
 * A week's fact as each of its games (Q6, B-614): itself, or in a week of
 * two its first and then its second, the second's opponent and result in
 * place of the first's. A result, a margin or a streak is a game's; a score,
 * a bench and a lineup are the week's, the same in both.
 */
export function gamesOf(t: TeamPeriodFact): TeamPeriodFact[] {
  const s = t.second;
  if (s === undefined || s === null) return [t];
  return [
    t,
    {
      ...t,
      opponentTeamId: s.opponentTeamId,
      opponentScore: s.opponentScore,
      result: s.result,
      opponentRecordBetter: s.opponentRecordBetter,
      second: null,
    },
  ];
}

/** A team's final games in order, a week of two games its two (`gamesOf`). */
export function teamGames(facts: SeasonFacts, teamId: string): TeamPeriodFact[] {
  return teamPeriods(facts, teamId).flatMap(gamesOf);
}

/** A team's final periods in order, played ones only. */
export function teamPeriods(facts: SeasonFacts, teamId: string): TeamPeriodFact[] {
  const out: TeamPeriodFact[] = [];
  for (const p of facts.periods) {
    if (!p.final) continue;
    const t = p.teams.find((x) => x.teamId === teamId);
    if (t && t.played) out.push(t);
  }
  return out;
}

export function longestRun(
  rows: readonly TeamPeriodFact[],
  test: (t: TeamPeriodFact) => boolean,
): number {
  let best = 0;
  let run = 0;
  for (const t of rows) {
    run = test(t) ? run + 1 : 0;
    if (run > best) best = run;
  }
  return best;
}

/** A settled week this team played, against somebody or against the field. */
function playedAWeek(facts: SeasonFacts, teamId: string): boolean {
  return facts.periods.some((p) => p.final && p.teams.some((t) => t.teamId === teamId && t.played));
}

/**
 * Whether any settled week was played against an opponent. A league that
 * plays the field (all-play, total points; PLAN-2026-10-16) has none.
 */
function hasOpponents(facts: SeasonFacts): boolean {
  if (facts.format !== undefined)
    return !['all-play', 'points-total', 'elimination'].includes(facts.format);
  // Facts that do not say: a settled week played with nobody to play.
  return !facts.periods.some(
    (p) =>
      p.final && p.teams.some((t) => t.played && t.opponentTeamId === null && t.result === null),
  );
}

function standing(facts: SeasonFacts, teamId: string) {
  return facts.standings.find((s) => s.teamId === teamId) ?? null;
}

function recordString(wins: number, losses: number, ties: number): string {
  return `${wins}–${losses}${ties ? `–${ties}` : ''}`;
}

export interface SeasonMetricValue {
  value: number;
  detail: Record<string, string | number>;
}

export const TEAM_SEASON_METRICS: Record<
  TeamSeasonMetricId,
  (facts: SeasonFacts, teamId: string) => SeasonMetricValue | null
> = {
  // A TEAM THAT HAS PLAYED: a record, or a settled week against the field,
  // which in a total-points league is the whole season and no record at all.
  pointsFor: (facts, teamId) => {
    const s = standing(facts, teamId);
    return s && (s.wins + s.losses + s.ties > 0 || playedAWeek(facts, teamId))
      ? { value: round1(s.pointsFor), detail: {} }
      : null;
  },
  // NOBODY TO SCORE AGAINST, nothing scored against: a league whose weeks
  // have no opponents has no points against to hand an award for.
  pointsAgainst: (facts, teamId) => {
    const s = standing(facts, teamId);
    return s && s.wins + s.losses + s.ties > 0 && hasOpponents(facts)
      ? { value: round1(s.pointsAgainst), detail: {} }
      : null;
  },
  wins: (facts, teamId) => {
    const s = standing(facts, teamId);
    return s ? { value: s.wins, detail: {} } : null;
  },
  losses: (facts, teamId) => {
    const s = standing(facts, teamId);
    return s ? { value: s.losses, detail: {} } : null;
  },
  bestWeek: (facts, teamId) => {
    const rows = teamPeriods(facts, teamId);
    if (!rows.length) return null;
    const best = rows.reduce((a, b) => (b.score > a.score ? b : a));
    return { value: best.score, detail: { period: best.period } };
  },
  // A STREAK IS GAMES, two a week in a week of two (Q6).
  winStreak: (facts, teamId) => {
    const rows = teamGames(facts, teamId);
    if (!rows.length) return null;
    return { value: longestRun(rows, (t) => t.result === 'win'), detail: {} };
  },
  lossStreak: (facts, teamId) => {
    const rows = teamGames(facts, teamId);
    if (!rows.length) return null;
    return { value: longestRun(rows, (t) => t.result === 'loss'), detail: {} };
  },
  benchPoints: (facts, teamId) => {
    // Only the weeks whose bench is known; none known is no answer, not 0.
    const rows = teamPeriods(facts, teamId).filter((t) => t.benchPoints !== null);
    if (!rows.length) return null;
    return { value: round1(rows.reduce((s, t) => s + (t.benchPoints ?? 0), 0)), detail: {} };
  },
  comeback: (facts, teamId) => {
    const half = Math.floor(facts.regularSeasonEnd / 2);
    const rows = teamGames(facts, teamId).filter((t) => t.period <= facts.regularSeasonEnd);
    const first = rows.filter((t) => t.period <= half);
    const second = rows.filter((t) => t.period > half);
    if (!first.length || !second.length) return null;
    const wins = (list: TeamPeriodFact[]) => list.filter((t) => t.result === 'win').length;
    const losses = (list: TeamPeriodFact[]) => list.filter((t) => t.result === 'loss').length;
    const ties = (list: TeamPeriodFact[]) => list.filter((t) => t.result === 'tie').length;
    if (wins(first) > losses(first)) return null;
    return {
      value: wins(second) - wins(first),
      detail: {
        firstHalf: recordString(wins(first), losses(first), ties(first)),
        secondHalf: recordString(wins(second), losses(second), ties(second)),
      },
    };
  },
  pickupPoints: (facts, teamId) => {
    let total = 0;
    let any = false;
    for (const p of facts.periods)
      for (const tx of p.transactions)
        if (tx.teamId === teamId && tx.kind !== 'trade') {
          any = true;
          total += tx.pointsSince;
        }
    return any ? { value: round1(total), detail: {} } : null;
  },
  trades: (facts, teamId) => {
    let n = 0;
    for (const p of facts.periods)
      for (const tx of p.transactions) if (tx.teamId === teamId && tx.kind === 'trade') n++;
    return { value: n, detail: {} };
  },
  // A TEAM THAT PICKED: none is no candidate, not a zero.
  pickemPoints: (facts, teamId) => {
    const points = facts.pickem?.[teamId];
    return points === undefined ? null : { value: points, detail: {} };
  },
};

// ── Milestones ────────────────────────────────────────────────────────────

/** A team's season value for a milestone metric; minted awards feed the medal counts. */
export function milestoneSeasonValue(
  facts: SeasonFacts,
  teamId: string,
  metric: MilestoneMetricId,
  minted: readonly Minted[],
): number {
  const rows = teamPeriods(facts, teamId);
  // Wins, streaks, margins, giant kills and escapes are games' (Q6).
  const games = rows.flatMap(gamesOf);
  const mintedFor = (awardId: string | null) =>
    minted.filter(
      (m) =>
        m.scope === 'period' &&
        !m.supersededBy &&
        (awardId === null || m.awardId === awardId) &&
        m.winners.some((w) => w.teamId === teamId),
    ).length;
  switch (metric) {
    case 'wins':
      return games.filter((t) => t.result === 'win').length;
    case 'bestWeek':
      return rows.reduce((b, t) => Math.max(b, t.score), 0);
    case 'winStreak':
      return longestRun(games, (t) => t.result === 'win');
    case 'biggestMargin':
      return games.reduce(
        (b, t) =>
          t.result === 'win' && t.opponentScore !== null
            ? Math.max(b, round1(t.score - t.opponentScore))
            : b,
        0,
      );
    case 'ironStreak':
      // A week nobody wrote down neither makes nor breaks a streak.
      return longestRun(
        rows.filter((t) => t.emptyStarters !== null && t.inactiveStarters !== null),
        (t) => t.emptyStarters === 0 && t.inactiveStarters === 0,
      );
    case 'perfectWeeks':
      return rows.filter((t) => t.optimalPoints !== null && t.optimalPoints - t.score <= 0.5)
        .length;
    case 'claimsWon':
      return facts.periods.reduce(
        (n, p) =>
          n + p.transactions.filter((tx) => tx.teamId === teamId && tx.kind === 'claim').length,
        0,
      );
    case 'trades':
      return facts.periods.reduce(
        (n, p) =>
          n + p.transactions.filter((tx) => tx.teamId === teamId && tx.kind === 'trade').length,
        0,
      );
    case 'giantKills':
      return games.filter((t) => t.result === 'win' && t.opponentRecordBetter).length;
    case 'escapes':
      return games.filter(
        (t) => t.result === 'win' && t.opponentScore !== null && t.score - t.opponentScore < 3,
      ).length;
    case 'lowScores':
      return mintedFor('low-score');
    case 'weeklyAwards':
      return mintedFor(null);
    case 'playoffTrips':
      return facts.bracket.playoffTeamIds.includes(teamId) &&
        facts.currentPeriod > facts.regularSeasonEnd
        ? 1
        : 0;
    case 'titles':
      return facts.bracket.final && facts.bracket.championTeamId === teamId ? 1 : 0;
  }
}

/** What a past season contributes to a career metric. */
export function milestoneHistoryValue(team: PastSeasonTeam, metric: MilestoneMetricId): number {
  switch (metric) {
    case 'wins':
      return team.wins;
    case 'bestWeek':
      return team.bestWeek;
    case 'winStreak':
      return team.longestStreak;
    case 'biggestMargin':
      return team.biggestMargin;
    case 'ironStreak':
      return team.ironStreak;
    case 'perfectWeeks':
      return team.perfectWeeks;
    case 'claimsWon':
      return team.claimsWon;
    case 'trades':
      return team.tradesCompleted;
    case 'giantKills':
      return team.giantKills;
    case 'escapes':
      return team.escapes;
    case 'lowScores':
      return team.weeklyAwards['low-score'] ?? 0;
    case 'weeklyAwards':
      return Object.values(team.weeklyAwards).reduce((a, b) => a + b, 0);
    case 'playoffTrips':
      return team.playoffs ? 1 : 0;
    case 'titles':
      return team.champion ? 1 : 0;
  }
}

/** Max-style metrics take the best season; count-style ones add up. */
export function metricCombines(metric: MilestoneMetricId): 'sum' | 'max' {
  switch (metric) {
    case 'bestWeek':
    case 'winStreak':
    case 'biggestMargin':
    case 'ironStreak':
      return 'max';
    default:
      return 'sum';
  }
}
